// Styles
import { useDispatch, useSelector } from "react-redux";
import "./chat-item.scss";
// Tools
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
// Components
import { CircleLoader } from "../../../../../../../../../../shared/components/circle-loader/CircleLoader";
import { CompactTextInput } from "../../../../../../../../../../shared/components/inputs/compact-text-input/CompactTextInput";
import { DeleteConfirmModal } from "../delete-confirm-modal/DeleteConfirmModal";
// Actions
import {
	setIsLoading,
	setSessionData,
	setSessionId,
} from "../../../../../../../../../../shared/store/slices/session.slice";
// Types
import { RootState } from "../../../../../../../../../../shared/store/store";
// Service
import { deleteChatService } from "../../service/delete-chat.service";
import { updateChatTitleService } from "../../service/update-chat-title.service";
import { updateBumblebeeChatTitleService } from "../../service/update-bumblebee-chat-title.service";
// Query
import { useQueryClient } from "@tanstack/react-query";
// Utils
import {
	getSessionInputDraftTime,
	hasInputDraft,
	INPUT_DRAFT_CHANGED_EVENT,
} from "../../../dialog/utils/setInputDraft";
// Hooks
import { useRelativeTime } from "../../../../../../../../../../shared/hooks/useRelativeTime";
import {
	BUMBLEBEE_TITLE_PREFIX,
	buildBumblebeeDisplayTitle,
	formatUnreadCount,
	getBumblebeeTitleSuffix,
} from "../../../../../../../../../../shared/utils/bumblebeeTitle";

interface Props {
	title: string;
	date: string;
	updateDate: string;
	isActive: boolean;
	sessionId: string | null;
	canDelete?: boolean;
	isBumblebee?: boolean;
	hasUnread?: boolean;
	unreadCount?: number;
	campaignId?: string;
	onSetSessionId: (sessionId: string) => void;
}

export const ChatItem = ({
	title,
	date,
	updateDate,
	isActive,
	sessionId,
	canDelete = true,
	isBumblebee = false,
	hasUnread = false,
	unreadCount = 0,
	campaignId,
	onSetSessionId,
}: Props) => {
	// ---------------- Query
	const queryClient = useQueryClient();
	// ---------------- Dispatch
	const dispatch = useDispatch();
	// ---------------- Refs
	const chatItemRef = useRef<HTMLDivElement | null>(null);
	const optionsWrapperRef = useRef<HTMLDivElement | null>(null);
	const loaderWrapperRef = useRef<HTMLDivElement | null>(null);
	const titleWrapperRef = useRef<HTMLDivElement | null>(null);
	const inputWrapperRef = useRef<HTMLDivElement | null>(null);
	// ---------------- States
	const [isOptionsVisible, setIsOptionsVisible] = useState(false);
	const [loadingStatus, setLoadingStatus] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editTitle, setEditTitle] = useState(
		isBumblebee ? getBumblebeeTitleSuffix(title, campaignId ?? "") : title,
	);
	const [itemTitle, setItemTitle] = useState<string>(title);
	const [hasDraft, setHasDraft] = useState(false);
	const [draftTime, setDraftTime] = useState<string | null>(null);
	const relativeDraftTime = useRelativeTime(draftTime);
	const globalSessionId = useSelector(
		(state: RootState) => state.session.sessionId,
	);
	const userId = useSelector(
		(state: RootState) => state.user?.buyer_id ?? null,
	);
	// ---------------- Functions
	const onHandleDelete = async (
		deletingSessionId: string,
		buyerUserId: string,
	) => {
		if (!deletingSessionId || !buyerUserId) return;
		setLoadingStatus(true);
		dispatch(setSessionData(null));
		if (deletingSessionId === globalSessionId) {
			dispatch(setSessionId(null));
			dispatch(setIsLoading(false));
		}
		const result = await deleteChatService(deletingSessionId, buyerUserId);

		if (!result || !result.success) {
			console.log("Помилка під час видалення");
		}

		// Прячем элемент
		gsap.to(chatItemRef.current, {
			height: 0,
			duration: 0.2,
			ease: "power1.out",
			onComplete: () => {
				// Перевызываем запрос и обновляем кэш
				queryClient.invalidateQueries({
					queryKey: ["sessions-data"],
				});
			},
		});
	};
	// Обновляем заголовок чата
	const onUpdateChatTitle = async (
		targetSessionId: string,
		userId: string,
		nextTitle: string,
	) => {
		setLoadingStatus(true);
		const result = isBumblebee
			? await updateBumblebeeChatTitleService(
					targetSessionId,
					buildBumblebeeDisplayTitle(nextTitle),
				)
			: await updateChatTitleService(targetSessionId, userId, nextTitle);
		if (!result || !result.success) {
			console.log("Помилка під час оновлення заголовку чата");
		}
		setLoadingStatus(false);
		setIsEditMode(false);
		setItemTitle(
			isBumblebee ? buildBumblebeeDisplayTitle(editTitle) : editTitle,
		);
		if (isBumblebee) {
			queryClient.invalidateQueries({
				queryKey: ["bumblebee-sessions-data"],
			});
		} else {
			queryClient.invalidateQueries({
				queryKey: ["sessions-data"],
			});
		}
	};
	const onTitleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== "Enter" || !isEditMode) return;
		event.preventDefault();
		if (sessionId === null) return;
		if (isBumblebee) {
			onUpdateChatTitle(sessionId, "", editTitle);
			return;
		}
		if (userId !== null) {
			onUpdateChatTitle(sessionId, String(userId), editTitle);
		}
	};
	// Клик по трем точкам
	const onHandleOptionsClick = () => {
		if (!isOptionsVisible) {
			setIsOptionsVisible(true);
			gsap.fromTo(
				optionsWrapperRef.current,
				{
					opacity: 0,
					x: 50,
				},
				{
					opacity: 1,
					x: 0,
					duration: 0.3,
					ease: "elastic.out(1.4, 1.6)",
				},
			);
		} else {
			gsap.fromTo(
				optionsWrapperRef.current,
				{
					opacity: 1,
					x: 0,
				},
				{
					opacity: 0,
					x: 50,
					duration: 0.15,
					ease: "power1.out",
					onComplete: () => {
						setIsOptionsVisible(false);
					},
				},
			);
		}
	};
	// ---------------- Effects
	useEffect(() => {
		setItemTitle(title);
		if (!isEditMode) {
			setEditTitle(
				isBumblebee
					? getBumblebeeTitleSuffix(title, campaignId ?? "")
					: title,
			);
		}
	}, [title, isBumblebee, campaignId, isEditMode]);

	// Убираем опции при клике вне кнопок и инпута
	useEffect(() => {
		if (!isOptionsVisible) return;
		const handleDocumentClick = (e: MouseEvent) => {
			const target = e.target as Node | null;
			if (!optionsWrapperRef.current) return;
			const isInsideInput = inputWrapperRef.current?.contains(target) ?? false;
			const isInsideOptions = optionsWrapperRef.current.contains(target);

			if (target && !isInsideOptions && !isInsideInput) {
				gsap.fromTo(
					optionsWrapperRef.current,
					{
						opacity: 1,
						x: 0,
					},
					{
						opacity: 0,
						x: 50,
						duration: 0.15,
						onComplete: () => {
							setIsOptionsVisible(false);
							if (isEditMode) setIsEditMode(false);
						},
					},
				);
			}
		};
		document.addEventListener("click", handleDocumentClick);
		return () => document.removeEventListener("click", handleDocumentClick);
	}, [isOptionsVisible, isEditMode]);

	useEffect(() => {
		const syncDraftMarker = () => {
			const draftExists = sessionId !== null && hasInputDraft(sessionId);
			setHasDraft(draftExists);
			setDraftTime(
				draftExists ? getSessionInputDraftTime(sessionId as string) : null,
			);
		};

		syncDraftMarker();
		window.addEventListener(INPUT_DRAFT_CHANGED_EVENT, syncDraftMarker);
		return () =>
			window.removeEventListener(INPUT_DRAFT_CHANGED_EVENT, syncDraftMarker);
	}, [sessionId]);

	const showUnreadBadge =
		hasUnread &&
		!isActive &&
		unreadCount > 0 &&
		!isOptionsVisible &&
		!isEditMode;
	const unreadBadgeLabel = formatUnreadCount(unreadCount);

	return (
		<div
			ref={chatItemRef}
			onClick={() => {
				sessionId && onSetSessionId(sessionId);
				// Если такой же id - не обновляем
				if (sessionId !== null && sessionId !== globalSessionId) {
					dispatch(setIsLoading(true));
					dispatch(setSessionData(null));
				}
			}}
			className={`chat-item ${isActive ? "active" : ""} ${
				hasUnread && !isActive ? "chat-item--unread" : ""
			} ${showUnreadBadge ? "chat-item--has-unread-badge" : ""}`}>
			{loadingStatus && (
				<div ref={loaderWrapperRef} className='chat-item__loader-wrapper'>
					<div className='chat-item__loader-wrapper_bg'></div>
					<div className='chat-item__loader'>
						<CircleLoader size='small' />
					</div>
				</div>
			)}
			<div className='chat-item__content'>
				<div className='chat-item__title'>
					{isEditMode ? (
						isBumblebee ? (
							<div
								ref={inputWrapperRef}
								className='chat-item__title-input-wrapper chat-item__title-input-wrapper--bumblebee'>
								<span className='chat-item__title-prefix'>
									{BUMBLEBEE_TITLE_PREFIX}
								</span>
								<CompactTextInput
									value={editTitle}
									className='chat-item__title-input'
									actionInput={(value) => setEditTitle(value)}
									onKeyDown={onTitleInputKeyDown}
								/>
							</div>
						) : (
							<div
								ref={inputWrapperRef}
								className='chat-item__title-input-wrapper'>
								<CompactTextInput
									value={editTitle}
									className='chat-item__title-input'
									actionInput={(value) => setEditTitle(value)}
									onKeyDown={onTitleInputKeyDown}
								/>
							</div>
						)
					) : (
						<div className='chat-item__title-text'>{itemTitle}</div>
					)}
				</div>
				<div className='chat-item__bottom'>
					<div className='chat-item__date'>{updateDate}</div>
					{hasDraft && <div className='chat-item__draft-marker'></div>}
					{hasDraft && relativeDraftTime && (
						<span className='chat-item__draft-time'>
							({relativeDraftTime})
						</span>
					)}
				</div>
			</div>
			{showUnreadBadge && (
				<div
					className='chat-item__unread-badge'
					aria-label={`Непрочитаних повідомлень: ${unreadBadgeLabel}`}>
					{unreadBadgeLabel}
				</div>
			)}
			<div
				ref={optionsWrapperRef}
				className={`chat-item__menu ${isOptionsVisible ? "visible" : ""}`}>
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (!isEditMode && isBumblebee) {
							setEditTitle(
								getBumblebeeTitleSuffix(itemTitle, campaignId ?? ""),
							);
						}
						setIsEditMode(!isEditMode);
						// Обновляем заголовок чата
						if (isEditMode && sessionId !== null) {
							if (isBumblebee) {
								onUpdateChatTitle(sessionId, "", editTitle);
							} else if (userId !== null) {
								onUpdateChatTitle(sessionId, String(userId), editTitle);
							}
						}
					}}
					className={`chat-item__menu-edit ${isEditMode ? "save" : ""}`}>
					{isEditMode ? (
						<img src='/img/icons/save.png' alt='save icon' />
					) : (
						<img src='/img/icons/edit.png' alt='edit icon' />
					)}
				</button>
				<button
					onClick={(e) => {
						e.stopPropagation();
						setIsDeleteModalOpen(true);
					}}
					className='chat-item__menu-delete'
					style={{ display: canDelete ? undefined : "none" }}>
					<img src='/img/icons/delete.png' alt='delete icon' />
				</button>
			</div>
			<button
				onClick={(e) => {
					e.stopPropagation();
					onHandleOptionsClick();
				}}
				className='chat-item__options'>
				<img src='/img/icons/more.png' alt='more icon' />
			</button>
			<DeleteConfirmModal
				isOpen={isDeleteModalOpen}
				title={itemTitle}
				isLoading={loadingStatus}
				onCancel={() => setIsDeleteModalOpen(false)}
				onConfirm={() => {
					setIsDeleteModalOpen(false);
					if (sessionId !== null && userId !== null) {
						onHandleDelete(sessionId, String(userId));
					}
				}}
			/>
		</div>
	);
};
